import * as Sentry from '@sentry/nextjs';
import safeStringify from 'fast-safe-stringify';
import { Request } from '@/components/authored-page-routes/authored-page-routes.interface';
import { COOKIE_DELTA, LoggedInStatus, PLP_QUERY_PARAM_KEYS } from '@/data/constants';
import { getCartData } from '@/ssr/cart-data';
import { getSdmWebTokens } from '@/ssr/sdm-web-tokens';
import { decodeUrlParams, getBannerHostUrlSSR, getLoggedInUserInfoSSR, getSiteId, isLoggedIn } from '@/utils';
import axios from 'axios';
import base64 from 'base-64';
import { serialize } from 'cookie';
import jwtDecode from 'jwt-decode';
import { GetServerSidePropsContext, GetServerSidePropsResult } from 'next';
import { getKameleoonClient, getKameleoonFlags } from './kameleoon/kameloon-sdk';
interface QueryParams {
  pageQueryParam: { from: string };
  sortQueryParam: { [key: string]: string };
  filtersQueryParam: { [key: string]: string[] };
  redirectQueryParam: { originalTarget: string | null };
}
type DecodedJWT = {
  exp: number;
  jti: string;
  pcid: string;
};

const handleError = <T>(val: PromiseSettledResult<T>): T | null => {

  if (val.status === 'rejected') {
    console.error(safeStringify({
      message: val.reason?.message,
      name: val.reason?.name,
      stack: val.reason?.stack,
      response: val.reason?.cause?.response
    }));

    return null;
  }

  return val.value;
};

export const handleErrorResponse = (response: any, bffData?: any) => {
  const errorMessage = {
    status: response.status,
    statusText: response.statusText,
    ...bffData,
  };

  return {
    error: errorMessage,
    isError: !response.ok,
    viewDefinition: null,
  };
};

export const fetchDataAndHandleErrors = async (context: any, getViewDefinitionFn: any, pageType?: string) => {
  const visitorCode = context?.req?.cookies.kameleoonVisitorCode || '';
  const ciamAccessToken = context?.req?.cookies?.ciamAccessToken;
  const userInfo = getLoggedInUserInfoSSR(ciamAccessToken);
  // Use PCID for logged-in users, visitor code for anonymous users
  const warehouseKey = userInfo?.pcid || visitorCode;

  //initialize Kameleoon SDK - experimentation tool for feature flags
  const kamClient = await getKameleoonClient();
  let activeKamFeatureFlags = null;

  if (kamClient) {
    // Only fetch Kameleoon flags if we have a valid visitor code
    if (visitorCode) {
      console.log('Kameleoon: Using visitor code from cookie (server-side)');
      activeKamFeatureFlags = await getKameleoonFlags(visitorCode, warehouseKey);
    } else {
      console.log('Kameleoon: No visitor code available on server side, will fetch on client-side');
      // Kameleoon flags will be fetched on client side when visitor code becomes available
    }
  } else {
    console.warn('WARNING:: Unable to fetch Kameleoon instance!');
  }

  // Get results concurrently
  const [viewDefinitionResults, themeTokens, cartData] = await Promise.allSettled([
    pageType
      ? getViewDefinitionFn(context, pageType, activeKamFeatureFlags)
      : getViewDefinitionFn(context, activeKamFeatureFlags),
    getSdmWebTokens(),
    getCartData(context),
  ]);

  // 404 page, a very specific error where midtier sends back an empty string for an unknown pageId, and we try to
  // serialize it as JSON
  if (
    viewDefinitionResults.status === 'rejected' &&
    viewDefinitionResults.reason instanceof Error &&
    viewDefinitionResults.reason.name === 'FetchError' &&
    (viewDefinitionResults.reason as Record<string, any>).type === 'invalid-json'
  ) {
    return { viewDefinitionResults: null, themeTokens: null, cartData: null, kamFlags: null };
  }

  return {
    viewDefinitionResults: handleError(viewDefinitionResults),
    themeTokens: handleError(themeTokens),
    cartData: handleError(cartData),
    kamFlags: activeKamFeatureFlags,
  };
};

export const getLoggedInStatusSSR = (req: Request): string => {
  return isLoggedIn(req) ? LoggedInStatus.CURRENT : LoggedInStatus.ANONYMOUS;
};

export const checkIfCookieExpiredSSR = (modifiedDate: string | undefined, req: Request): boolean => {
  return (
    getLoggedInStatusSSR(req) === LoggedInStatus.ANONYMOUS &&
    new Date().getTime() > Number(modifiedDate) + Number(COOKIE_DELTA.TWO_DAYS)
  );
};

export const getExistingCartGuidSSR = (req: Request): string => {
  const siteId = getSiteId(req);
  const cartIdHelios = `${siteId}-cart-helios`;
  const cartIdHeliosModifiedDate = `${siteId}-cart-helios-modified-date`;
  const heliosGuid = req?.cookies?.[cartIdHelios];
  const modifiedDate = req?.cookies?.[cartIdHeliosModifiedDate];
  // modifiedDate lets us know the last time an item was added to the bag as an ANNOYMOUS user.
  const cookieExpired = checkIfCookieExpiredSSR(modifiedDate, req);

  return cookieExpired ? '' : heliosGuid || '';
};

export const withAuthentication = (getServerSidePropsFn: any) => (ctx: { req: Request }) => {
  const isLoggedInUser = isLoggedIn(ctx.req);
  if (!isLoggedInUser) {
    const hostname = ctx?.req?.headers?.host || '';
    const banner = getBannerHostUrlSSR(hostname);
    const login = `${banner}/login`;
    return {
      redirect: {
        permanent: false,
        destination: login,
      },
    };
  }

  return getServerSidePropsFn(ctx);
};

export async function refreshAccessTokenIfNeeded(context: GetServerSidePropsContext) {
  let accessToken = context.req.cookies?.ciamAccessToken;
  const refreshToken = context.req.cookies?.sso_refresh_token;
  let isPCIDTokenValid = false;
  const clientId = process.env.NEXT_PUBLIC_PCID_CLIENT_ID || null;
  const clientSecret = process.env.NEXT_PUBLIC_PCID_CLIENT_SECRET || null;
  const baseURL = process.env.NEXT_PUBLIC_PCID_BASE_URL || null;

  if (accessToken) {
    try {
      const decoded = jwtDecode<DecodedJWT>(accessToken);
      // Set the user
      Sentry.setUser({ id: decoded.pcid });
      // Add JWT metadata as context
      Sentry.setContext("saving auth_token", {
        tokenId: decoded.jti,
        exp: new Date(decoded.exp * 1000).toISOString(), // human-readable
      });

      isPCIDTokenValid = Date.now() / 1000 < decoded.exp;
    } catch {
      console.error('Failed to decode the access token on server');
      Sentry.addBreadcrumb({ message: 'Failed to decode the access token on server' });
    }
  }

  if (!isPCIDTokenValid && refreshToken) {
    try {
      Sentry.addBreadcrumb({ message: 'Refreshing access token on server' });
      const response = await axios.post(
        `${baseURL}/oauth2/v1/token`,
        new URLSearchParams({
          grant_type: 'refresh_token',
          refresh_token: refreshToken,
        }),
        {
          headers: {
            Authorization: `Basic ${base64.encode(`${clientId}:${clientSecret}`)}`,
            'Content-Type': 'application/x-www-form-urlencoded',
          },
        }
      );

      accessToken = response.data.access_token;

      // Set the new cookies on the response
      context.res.setHeader('Set-Cookie', [
        serialize('ciamAccessToken', response.data.access_token, {
          maxAge: 2 * 60 * 60, // 2 hours
          httpOnly: false,
          path: '/',
          secure: true,
        }),
        serialize('sso_refresh_token', response.data.refresh_token || refreshToken, {
          httpOnly: false,
          maxAge: 180 * 24 * 60 * 60, // 6 months
          path: '/',
          secure: true,
        }),
      ]);

      Sentry.addBreadcrumb({ message: 'Token refreshed successfully on server' });

      // Inject the new access token into the current request's cookies
      // so downstream logic in the same request sees the token
      // this can't be done in the middleware, it only updates the token for current req and not the next req
      context.req.cookies.ciamAccessToken = accessToken;
    } catch (e) {
      Sentry.addBreadcrumb({ message: 'Token refreshed failed on server' });
      Sentry.captureException(e);
      if (axios.isAxiosError(e)) {
        console.error('Failed to refresh access token: ', safeStringify({
          status: e.response?.status,
          data: e.response?.data,
        }));
      } else {
        console.error('Failed to refresh access token: ', safeStringify(e));
      }
    }
  }

  return accessToken;
}

export function withTokenRefresh<P>(
  getServerSidePropsFn: (ctx: GetServerSidePropsContext) => Promise<GetServerSidePropsResult<P>>
) {
  return async (ctx: GetServerSidePropsContext): Promise<GetServerSidePropsResult<P>> => {
    await refreshAccessTokenIfNeeded(ctx);
    return getServerSidePropsFn(ctx);
  };
}

export const getQueryParams = (query: Record<string, any>): QueryParams => {
  const { page = 1, sort, ...filters } = query;
  const PAGE_SIZE = 36;
  const totalResultsQty = page > 1 ? ((page - 1) * PAGE_SIZE).toString() : '0';

  // Process pagination
  const pageQueryParam = { from: totalResultsQty };

  // Process Sort
  let sortQueryParam = {};

  if (sort !== undefined) {
    sortQueryParam = { [sort]: '' };
  }

  // Process filters
  const { SEARCH_REDIRECT_PARAM_KEY } = PLP_QUERY_PARAM_KEYS;
  const excludedKeys = Object.values(PLP_QUERY_PARAM_KEYS);

  const filtersQueryParam: { [key: string]: string[] } = {};
  for (const [key, value] of Object.entries(filters)) {
    if (!(excludedKeys.includes(key as any) || key?.includes('-'))) {
      filtersQueryParam[key] = Array.isArray(value)
        ? value.map((v: string) => decodeUrlParams(v))
        : [decodeUrlParams(value)];
    }
  }

  const redirectQueryParam = {
    originalTarget: query?.[SEARCH_REDIRECT_PARAM_KEY] || null,
  };

  return { filtersQueryParam, sortQueryParam, pageQueryParam, redirectQueryParam };
};
